/**
 * What the start gate looks like: the black screen `TITLE.TTM`'s frame 0 opens
 * with anyway, holding one blinking line in the shell's own `FONT1` with the
 * same engraved orange the Main Menu inks its button labels in, so the one
 * screen the original never had still belongs to it.
 *
 * Pure — a `DrawOp` list against palette indices, no canvas — so the copy, the
 * centring and the blink phase are all assertable, the same way `menu-chrome`'s
 * measured geometry is.
 */

import { type DrawOp, fill, type FontId, LOGICAL_HEIGHT, LOGICAL_WIDTH, type TextStyle } from "../render/draw-ops.ts";
import { ORANGE_DARKEST, ORANGE_HIGHLIGHT, ORANGE_LABEL, TAN_233 } from "../render/ps-palette.ts";
import { centerTextX, type FontAtlas, glyphFor, measureText } from "../render/text-metrics.ts";
import { type GateState, gateVisible } from "./start-gate.ts";

export const GATE_PROMPT = "CLICK OR PRESS ANY KEY";
export const GATE_LOADING = "LOADING";
export const GATE_FONT: FontId = "FONT1";

/** Dead centre of the frame for a 12-row glyph plus its one-row emboss. */
export const GATE_TEXT_Y = 93;
export const GATE_TEXT_CENTER = LOGICAL_WIDTH / 2;

/**
 * VGA's own attribute blink: the vertical refresh divided by 32, ≈1.875 Hz —
 * the rate every blinking character on a 1990 DOS screen ran at.
 */
export const GATE_BLINK_PERIOD_MS = 1000 / 1.875;
/** After the gesture the prompt stops blinking and holds; the loading line only appears if the wait becomes noticeable. */
export const GATE_LOADING_DELAY_MS = 300;
export const GATE_DOT_INTERVAL_MS = GATE_BLINK_PERIOD_MS / 2;
export const GATE_MAX_DOTS = 3;

const PROMPT_STYLE: TextStyle = { body: ORANGE_LABEL, shadow: ORANGE_DARKEST, highlight: ORANGE_HIGHLIGHT };
/** Flat, one step down the tan ramp: the wait should read as quieter than the invitation. */
const LOADING_STYLE: TextStyle = { body: TAN_233 };

export type GateView = "prompt" | "loading";

export interface GateFrame {
  readonly view: GateView;
  /** `false` only during the blink's dark half — the gate's sole animation before the gesture. */
  readonly blinkOn: boolean;
  readonly dots: number;
}

export function gateBlinkOn(elapsedMs: number): boolean {
  return elapsedMs % GATE_BLINK_PERIOD_MS < GATE_BLINK_PERIOD_MS / 2;
}

/**
 * `undefined` once the gate is done — `playing` and `menu` draw the intro and
 * the Main Menu instead. `elapsedInPhaseMs` restarts at each phase change, so
 * the acknowledged-but-waiting hold is measured from the gesture itself.
 */
export function gateFrameAt(state: GateState, elapsedInPhaseMs: number): GateFrame | undefined {
  if (!gateVisible(state)) return undefined;
  if (state.phase === "gate") return { view: "prompt", blinkOn: gateBlinkOn(elapsedInPhaseMs), dots: 0 };
  const waited = elapsedInPhaseMs - GATE_LOADING_DELAY_MS;
  if (waited < 0) return { view: "prompt", blinkOn: true, dots: 0 };
  return { view: "loading", blinkOn: true, dots: Math.floor(waited / GATE_DOT_INTERVAL_MS) % (GATE_MAX_DOTS + 1) };
}

/**
 * The dots trail the word rather than being centred with it, so the line does
 * not jitter left and right as they cycle.
 */
function loadingOps(frame: GateFrame, font: FontAtlas): DrawOp[] {
  const x = centerTextX(font, GATE_LOADING, GATE_TEXT_CENTER);
  const ops: DrawOp[] = [{ kind: "text", font: GATE_FONT, text: GATE_LOADING, x, y: GATE_TEXT_Y, style: LOADING_STYLE }];
  if (frame.dots === 0) return ops;
  const gap = glyphFor(font, 0x20)?.width ?? 0;
  ops.push({
    kind: "text",
    font: GATE_FONT,
    text: ".".repeat(frame.dots),
    x: x + measureText(font, GATE_LOADING) + gap,
    y: GATE_TEXT_Y,
    style: LOADING_STYLE,
  });
  return ops;
}

export function gateScreenOps(frame: GateFrame, font: FontAtlas): DrawOp[] {
  const ops: DrawOp[] = [fill({ x: 0, y: 0, w: LOGICAL_WIDTH, h: LOGICAL_HEIGHT }, 0)];
  if (frame.view === "loading") return [...ops, ...loadingOps(frame, font)];
  if (!frame.blinkOn) return ops;
  ops.push({
    kind: "text",
    font: GATE_FONT,
    text: GATE_PROMPT,
    x: centerTextX(font, GATE_PROMPT, GATE_TEXT_CENTER),
    y: GATE_TEXT_Y,
    style: PROMPT_STYLE,
  });
  return ops;
}
